import app from '@frontastic/catwalk/src/js/app/app';
import cloneDeep from 'lodash/cloneDeep';
import { isArray, isEmptyArray } from './assertions';
import { sortArrayByLabel } from '@srcDS/components/organisms/productActions/utils/sortOrder';

export const getWishlistRemovingActionContext = (store, lineItemId) => {
  const lineItem = store.getState().wishlist?.wishlist?.data?.lineItems.find((item) => item.lineItemId === lineItemId);
  return { sku: lineItem?.variant.sku };
};

export const getCartRemovingActionContext = (store, lineItemId) => {
  const lineItem = store.getState().cart?.cart?.data?.lineItems.find((item) => item.lineItemId === lineItemId);
  return { sku: lineItem?.variant.sku };
};

export const formatVariants = (variants = []) => {
  return variants.map((variant) => {
    return {
      ...variant,
      projectSpecificData: {
        availableQuantity: variant.availability,
      },
      attributes: {
        size: {
          label: variant.size,
        },
      },
    };
  });
};

export const formattedImagesArray = (initialVariant) => {
  const images = initialVariant?.images ?? initialVariant?.projectSpecificData?.images;
  const sortedImages = images && images.length && sortArrayByLabel(images);
  const initialArr = sortedImages
    ? sortedImages.map((image, index) => {
        return {
          id: initialVariant.sku + index,
          url: image,
        };
      })
    : [];

  const videoObj = {
    id: initialVariant.sku + '_video',
    videoUrl: initialVariant?.attributes?.productvideourl,
  };

  if (initialVariant?.attributes?.primaryvideoplp && initialVariant?.attributes?.productvideourl) {
    return [videoObj, ...initialArr];
  } else if (!initialVariant?.attributes?.primaryvideoplp && initialVariant?.attributes?.productvideourl) {
    const copiedArr = [...initialArr];
    copiedArr.splice(2, 0, videoObj);
    return copiedArr;
  } else {
    return initialArr;
  }
};

const comparePrices = (item1, item2) => {
  const isValidItem1 = item1?.isOnStock && item1?.projectSpecificData?.availableQuantity > 0;
  const isValidItem2 = item2?.isOnStock && item2?.projectSpecificData?.availableQuantity > 0;

  if (isValidItem1 && !isValidItem2) {
    return -1;
  } else if (!isValidItem1 && isValidItem2) {
    return 1;
  } else if (isValidItem1 && isValidItem2) {
    const totalPrice1 = item1.projectSpecificData.prices.reduce((acc, price) => acc + price?.value?.centAmount || 0, 0);
    const totalPrice2 = item2.projectSpecificData.prices.reduce((acc, price) => acc + price?.value?.centAmount || 0, 0);

    return totalPrice1 - totalPrice2;
  }

  return 0;
};

const getSortedByPrice = (arr) => {
  return cloneDeep(arr)?.sort(comparePrices)?.[0];
};

export const pricesForGroup = (prices, vipPrices, loggedIn, customerGroupId) => {
  if (loggedIn) {
    const groupPrice = vipPrices?.filter((p) => p?.forRegistered && p?.customerGroupId === customerGroupId);
    return getPrices(groupPrice?.length ? groupPrice : vipPrices);
  } else {
    const filteredPrices = prices?.filter((p) => !p.forRegistered);
    return getPrices(filteredPrices);
  }
};

export const getPrices = (prices) => {
  const defaultPrice = {
    price: null,
    discountedPrice: null,
    onSale: false,
  };

  if (isArray(prices)) {
    const RRP_price = prices?.find(
      ({ custom }) => isEmptyArray(custom) || custom?.price_type === 'au-rrp' || custom?.price_type === 'nz-rrp',
    );

    const sale_price = prices?.find((price) => {
      const price_type = price?.custom?.price_type;
      return price_type === 'au-sale' || price_type === 'nz-sale';
    });

    // PRODUCT DISCOUNT + SALE PRICE
    if (!!sale_price && !!RRP_price) {
      return {
        price: RRP_price.value.centAmount,
        discountedPrice: sale_price.discounted.centAmount ?? sale_price.value.centAmount,
        onSale: true,
      };
    }
    // NORMAL PRICE
    if (!!RRP_price && !RRP_price.discounted && !RRP_price.discounted.length) {
      return {
        price: RRP_price.value.centAmount,
        discountedPrice: null,
        onSale: false,
      };
    }
    // PRODUCT DISCOUNT PRICE
    if (!!RRP_price && !!RRP_price.discounted && sale_price === undefined) {
      return {
        price: RRP_price.value.centAmount,
        discountedPrice: RRP_price.discounted.centAmount,
        onSale: false,
      };
    }

    return defaultPrice;
  }
  return defaultPrice;
};

export const transformNodeIdToPath = (nodeId) => {
  const router = app.getRouter();
  const route = 'node_' + nodeId;

  if (router.hasRoute(route)) {
    return router.path(route);
  }
  return nodeId;
};

export const parseDiscountItem = (data) => {
  if (!data) return;

  let color = '#b61e0a';
  let bgColor = 'rgba(236, 209, 198, 0.5)';

  const { name, description } = data;
  const colours = description.split(',');

  return {
    name,
    color: colours[0] ?? color,
    bgColor: colours[1] ?? bgColor,
    borderColor: colours?.[2] ?? bgColor,
  };
};

export const getHexColor = (hex) => {
  if (!hex) return null;
  const regex = /#(([0-9a-fA-F]{2}){3,4}|([0-9a-fA-F]){3,4})/g;
  if (hex.match(regex)) return hex;

  return null;
};

export const findOriginalIdDiscount = (id, discountCodesOriginal) => {
  if (!id || !discountCodesOriginal?.length) return;
  return discountCodesOriginal && discountCodesOriginal.find((d) => d.originalId === id)?.id;
};

export const sortSiblings = (siblings) => {
  if (!siblings.length) return [];
  return siblings.sort(
    (a, b) => a.colour?.localeCompare(b?.colour) || a.colorAttribute?.localeCompare(b?.colorAttribute),
  );
};

export const formatUrl = (url) => {
  if (!url) return '';
  if (typeof window === 'undefined') return url;

  const currentOrigin = window.location.origin;
  const match = url.match(/\/clothing.*/);

  if (match) {
    return `${currentOrigin}${match[0]}`;
  }
  return `${currentOrigin}${url.startsWith('/') ? url : `/${url}`}`;
};

export const getShippingPrice = (d, shippingMethodId, cartData) => {
  if (d.target === 'shipping' && d?.amount === 0) {
    const availableShippingMethods = cartData?.cart?.availableShippingMethods || cartData?.availableShippingMethods;
    if (!availableShippingMethods) return d.amount;

    const selectedShippingMethod = availableShippingMethods.find((m) => m?.shippingMethodId === shippingMethodId);
    const amount = selectedShippingMethod?.rates.find((m) => m.price)?.price ?? d?.amount;
    return `-${amount}`;
  }

  return `-${d?.amount}`;
};

export const findDiscount = (discounts, data) => {
  const { loggedIn, vipPrices, onSale, customerGroupId } = data;
  const disc = discounts ? [...discounts].sort((a, b) => b.sortOrder - a.sortOrder) : [];
  // Check if user has customType: vip
  const vipDiscounts = disc?.filter((discount) => discount?.customType === 'vip');
  // If user has customType: vip

  if (loggedIn && !!vipPrices?.length && !onSale && isArray(disc) && vipDiscounts?.length > 0) {
    const eligibleVipDiscounts = vipDiscounts?.filter(
      (vipDiscount) =>
        !vipDiscount?.['customer-groups']?.length || vipDiscount?.['customer-groups'].includes(customerGroupId),
    );
    return eligibleVipDiscounts?.length > 0 ? eligibleVipDiscounts[0] : {};
  } else {
    // If user hasn't customType: vip, but "regular" then render first available element with highest sortOrder
    return disc?.find((d) => d.customType === 'regular');
  }
};

export const getTags = (initialVariant, parentProduct, isSearchPage = false, addColorTag = true) => {
  const hasBadge = initialVariant.attributes.productimagebadge;
  const tagsArr = [initialVariant.attributes.colour && addColorTag && initialVariant.attributes.colour];

  if (hasBadge) {
    const newArr = hasBadge.split(',');
    return [...tagsArr, ...newArr];
  }

  return tagsArr;
};

export const getProductDetails = (product, isFromNosto, loggedIn, customerGroupId) => {
  const variants = product?.projectSpecificData.extendedSizes;
  const initialVariant = (variants && getSortedByPrice(variants)) || getSortedByPrice(product.variants);
  const {
    projectSpecificData: { prices = [], discount = {} },
    attributes: { prices: attrPrices = [], vip_prices: attrVipPrices = [], discounts: attrDiscounts = {} },
  } = initialVariant;

  const currentPrice = isFromNosto ? attrPrices : prices;
  const finalSale = initialVariant.attributes?.finalsale === 'true' || initialVariant.attributes?.finalsale === true;

  const {
    price: regularPrice,
    discountedPrice,
    onSale,
  } = isFromNosto ? pricesForGroup(currentPrice, attrVipPrices, loggedIn, customerGroupId) : getPrices(currentPrice);

  const currentDiscount = isFromNosto
    ? findDiscount(attrDiscounts, {
        loggedIn,
        vipPrices: attrVipPrices,
        onSale,
        customerGroupId,
      })
    : discount;

  return {
    variants,
    initialVariant,
    regularPrice,
    discountedPrice,
    onSale,
    currentDiscount,
    finalSale,
  };
};


export const handleize = (str) => {
  return str
    .toLowerCase()
    .replace(/[^\w\u00C0-\u024f]+/g, '-')
    .replace(/^-+|-+$/g, '');
};
